/**
 * RevenueCat client helpers — native iOS only.
 * Uses StoreKit 2 via @revenuecat/purchases-capacitor (configured in RevenueCat dashboard).
 */

'use client'

import {
  APPLE_PRODUCT_ID_PACK_LEADER,
  REVENUECAT_ENTITLEMENT_PACK_LEADER,
  REVENUECAT_OFFERING_IDENTIFIER,
  REVENUECAT_PACKAGE_IDENTIFIER,
  SUBSCRIPTION_PURCHASE_COMPLETED_REFRESHING_MESSAGE,
  SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE,
  sanitizePaywallUserMessage,
} from '@/lib/monetization/iapConstants'
import { isCapacitorIosApp } from '@/lib/auth/capacitorSignIn'
import { detectNativeIosForUpgrade } from '@/lib/monetization/detectUpgradePlatform'
import { logUpgradeFlow } from '@/lib/monetization/upgradeFlowDebugLog'
import {
  logUpgradeIapBlocked,
  logUpgradeIapTrace,
  summarizeCustomerInfoForUpgradeTrace,
} from '@/lib/monetization/upgradeIapTrace'
import {
  forceClearNativeInteractionLocks,
  setNativePaymentInProgress,
} from '@/lib/monetization/forceClearNativeInteractionLocks'
import { applyPackLeaderTierLocally } from '@/lib/monetization/refreshSubscriptionState'

export type RevenueCatInitState = 'idle' | 'initializing' | 'ready' | 'error'

let initState: RevenueCatInitState = 'idle'
let initError: string | null = null
let configuredUserId: string | null = null
let offeringsLoaded = false

function logIapFinalTrace(
  step: string,
  detail?: Record<string, unknown>
): void {
  if (detail) {
    console.log(`[IAPFinalTrace] ${step} ${JSON.stringify(detail)}`)
  } else {
    console.log(`[IAPFinalTrace] ${step}`)
  }
}

async function waitForRevenueCatReady(timeoutMs = 8000): Promise<boolean> {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    if (initState === 'ready') return true
    if (initState === 'error') return false
    await new Promise((resolve) => setTimeout(resolve, 100))
  }
  return initState === 'ready'
}

function summarizeApiKeyForTrace(
  apiKey: string | null
): Record<string, unknown> {
  if (!apiKey) return { configured: false }
  return {
    configured: true,
    length: apiKey.length,
    prefix: apiKey.slice(0, 4),
    suffix: apiKey.slice(-4),
  }
}

function isNativeIosRevenueCatContext(): boolean {
  return detectNativeIosForUpgrade() || isCapacitorIosApp()
}

function traceRcLog(step: string, detail?: Record<string, unknown>): void {
  console.log(
    `[UpgradeTrace9419bd7] ${step}` +
      (detail ? ` ${JSON.stringify(detail)}` : '')
  )
}

function traceRcFail(step: string, error: unknown): void {
  const err = error instanceof Error ? error : new Error(String(error))
  console.error(
    `[UpgradeTrace9419bd7] ${step} ${JSON.stringify({
      message: err.message,
      name: err.name,
      stack: err.stack,
    })}`
  )
}

export function getRevenueCatInitState(): {
  state: RevenueCatInitState
  error: string | null
} {
  return { state: initState, error: initError }
}

export function getOfferingsLoadedState(): boolean {
  return offeringsLoaded
}

function getAppleApiKey(): string | null {
  const key = process.env.NEXT_PUBLIC_REVENUECAT_APPLE_API_KEY?.trim()
  return key || null
}

type PackLeaderCustomerInfo = {
  entitlements?: {
    active?: Record<string, unknown>
    all?: Record<string, Record<string, unknown>>
  }
  activeSubscriptions?: string[]
  allPurchasedProductIdentifiers?: string[]
}

function productIdentifierFromActiveSubscription(
  customerInfo: PackLeaderCustomerInfo | null | undefined
): string | null {
  const subs = customerInfo?.activeSubscriptions ?? []
  if (subs.includes(APPLE_PRODUCT_ID_PACK_LEADER)) {
    return APPLE_PRODUCT_ID_PACK_LEADER
  }
  return subs[0] ?? null
}

function customerInfoGrantsPackLeaderAccess(
  customerInfo: PackLeaderCustomerInfo | null | undefined,
  context?: string
): boolean {
  if (!customerInfo) return false

  if (context) {
    logUpgradeIapTrace('customerInfo entitlement snapshot', {
      context,
      ...summarizeCustomerInfoForUpgradeTrace(customerInfo),
      expectedEntitlementId: REVENUECAT_ENTITLEMENT_PACK_LEADER,
      expectedProductId: APPLE_PRODUCT_ID_PACK_LEADER,
      productIdentifierFromActiveSubscription:
        productIdentifierFromActiveSubscription(customerInfo),
    })
  }

  if (customerInfo.entitlements?.active?.[REVENUECAT_ENTITLEMENT_PACK_LEADER]) {
    return true
  }

  for (const key of Object.keys(customerInfo.entitlements?.active ?? {})) {
    if (key.toLowerCase().replace(/[\s-]+/g, '_') === REVENUECAT_ENTITLEMENT_PACK_LEADER) {
      return true
    }
  }

  if ((customerInfo.activeSubscriptions ?? []).includes(APPLE_PRODUCT_ID_PACK_LEADER)) {
    if (context) {
      logUpgradeIapTrace('pack_leader granted via activeSubscriptions product match', {
        context,
        productId: APPLE_PRODUCT_ID_PACK_LEADER,
      })
    }
    return true
  }

  const allEnt = customerInfo.entitlements?.all?.[
    REVENUECAT_ENTITLEMENT_PACK_LEADER
  ] as Record<string, unknown> | undefined
  if (allEnt) {
    if (allEnt.isActive === true) return true
    const expires = allEnt.expirationDate ?? allEnt.expiresDate
    if (typeof expires === 'string' && new Date(expires).getTime() > Date.now()) {
      return true
    }
  }

  return false
}

function hasPackLeaderEntitlement(
  customerInfo: PackLeaderCustomerInfo | null | undefined
): boolean {
  return customerInfoGrantsPackLeaderAccess(customerInfo)
}

function purchaseErrorText(error: unknown): string {
  const err = error as {
    message?: string
    readableErrorCode?: string
    underlyingErrorMessage?: string
  }
  return [err?.message, err?.readableErrorCode, err?.underlyingErrorMessage]
    .filter(Boolean)
    .join(' ')
}

function isAlreadySubscribedPurchaseError(error: unknown): boolean {
  const err = error as { code?: string | number }
  const code = err?.code != null ? String(err.code) : ''
  const text = purchaseErrorText(error)
  return (
    code === '6' ||
    code === 'PRODUCT_ALREADY_PURCHASED_ERROR' ||
    /already (?:subscribed|purchased)|currently subscribed|you'?re currently subscribed|you are currently subscribed/i.test(
      text
    )
  )
}

async function fetchActivePackLeaderCustomerInfo(
  context: string
): Promise<PackLeaderCustomerInfo | null> {
  try {
    const { Purchases } = await loadPurchasesModule()
    const { customerInfo } = await Purchases.getCustomerInfo()
    const grants = customerInfoGrantsPackLeaderAccess(
      customerInfo,
      `${context}_getCustomerInfo`
    )
    logUpgradeIapTrace('getCustomerInfo after purchase attempt', {
      context,
      ...summarizeCustomerInfoForUpgradeTrace(customerInfo),
      hasPackLeader: grants,
    })
    return grants ? customerInfo : null
  } catch (infoError) {
    logUpgradeIapTrace('getCustomerInfo after purchase attempt failed', {
      context,
      message:
        infoError instanceof Error ? infoError.message : String(infoError),
    })
    return null
  }
}

async function resolveActivePackLeaderCustomerInfo(
  context: string
): Promise<PackLeaderCustomerInfo | null> {
  const fromCustomerInfo = await fetchActivePackLeaderCustomerInfo(context)
  if (fromCustomerInfo) return fromCustomerInfo

  try {
    const { Purchases } = await loadPurchasesModule()
    logUpgradeIapTrace('restorePurchases for pack_leader resolution', { context })
    const { customerInfo } = await Purchases.restorePurchases()
    const grants = customerInfoGrantsPackLeaderAccess(
      customerInfo,
      `${context}_restorePurchases`
    )
    logUpgradeIapTrace('restorePurchases for pack_leader resolution result', {
      context,
      ...summarizeCustomerInfoForUpgradeTrace(customerInfo),
      hasPackLeader: grants,
    })
    return grants ? customerInfo : null
  } catch (restoreError) {
    logUpgradeIapTrace('restorePurchases for pack_leader resolution failed', {
      context,
      message:
        restoreError instanceof Error
          ? restoreError.message
          : String(restoreError),
    })
    return null
  }
}

async function resolveActivePackLeaderAfterPurchaseFailure(
  error: unknown
): Promise<PackLeaderCustomerInfo | null> {
  const fromCustomerInfo = await fetchActivePackLeaderCustomerInfo(
    'purchase_failure_getCustomerInfo'
  )
  if (fromCustomerInfo) return fromCustomerInfo

  if (!isAlreadySubscribedPurchaseError(error)) return null

  try {
    const { Purchases } = await loadPurchasesModule()
    logUpgradeIapTrace('restorePurchases after already-subscribed', {})
    const { customerInfo } = await Purchases.restorePurchases()
    const grants = customerInfoGrantsPackLeaderAccess(
      customerInfo,
      'purchase_failure_already_subscribed_restore'
    )
    logUpgradeIapTrace('restorePurchases after already-subscribed result', {
      ...summarizeCustomerInfoForUpgradeTrace(customerInfo),
      hasPackLeader: grants,
    })
    return grants ? customerInfo : null
  } catch (restoreError) {
    logUpgradeIapTrace('restorePurchases after already-subscribed failed', {
      message:
        restoreError instanceof Error
          ? restoreError.message
          : String(restoreError),
    })
    return null
  }
}

/** Re-fetch RevenueCat customerInfo and apply local Pack Leader tier if entitled. */
export async function tryApplyPackLeaderTierFromRevenueCat(
  context: string
): Promise<{ applied: boolean; customerInfo?: PackLeaderCustomerInfo }> {
  const customerInfo = await resolveActivePackLeaderCustomerInfo(context)
  if (!customerInfo) {
    logUpgradeIapTrace('tryApplyPackLeaderTierFromRevenueCat no entitlement', {
      context,
    })
    return { applied: false }
  }

  const cacheResult = applyPackLeaderTierLocally(context)
  logUpgradeIapTrace('tryApplyPackLeaderTierFromRevenueCat applied', {
    context,
    writeOk: cacheResult.writeOk,
    before: cacheResult.before,
    after: cacheResult.after,
  })
  return { applied: true, customerInfo }
}

function sanitizePurchaseFailureMessage(message: string): string {
  return sanitizePaywallUserMessage(message, 'purchase_failed')
}

function findPackLeaderPackage(offering: {
  identifier?: string
  availablePackages?: Array<{
    identifier: string
    product?: { identifier: string; priceString?: string | null }
  }>
} | null | undefined): {
  identifier: string
  product: { identifier: string; priceString?: string | null }
} | null {
  const packages = offering?.availablePackages ?? []
  if (!packages.length) return null

  const byPackageId = packages.find(
    (p) => p.identifier === REVENUECAT_PACKAGE_IDENTIFIER && p.product
  )
  if (byPackageId?.product) return byPackageId as NonNullable<typeof byPackageId>

  const byProductId = packages.find(
    (p) => p.product?.identifier === APPLE_PRODUCT_ID_PACK_LEADER
  )
  if (byProductId?.product) return byProductId as NonNullable<typeof byProductId>

  const firstWithProduct = packages.find((p) => p.product)
  return firstWithProduct?.product
    ? (firstWithProduct as NonNullable<typeof firstWithProduct>)
    : null
}

async function loadPurchasesModule() {
  traceRcLog('loadPurchasesModule start')
  try {
    const mod = await import('@revenuecat/purchases-capacitor')
    traceRcLog('loadPurchasesModule success')
    return mod
  } catch (error) {
    traceRcFail('loadPurchasesModule fail', error)
    throw error
  }
}

/**
 * Configure RevenueCat and associate the Wagamour user id (call after sign-in on iOS).
 */
export async function initializeRevenueCat(appUserId: string): Promise<void> {
  if (!isNativeIosRevenueCatContext()) {
    logUpgradeIapBlocked('initializeRevenueCat_not_native_ios')
    traceRcLog('initializeRevenueCat skipped', { reason: 'not_native_ios' })
    return
  }
  if (typeof window === 'undefined') {
    logUpgradeIapBlocked('initializeRevenueCat_no_window')
    return
  }

  const apiKey = getAppleApiKey()
  logIapFinalTrace('initializeRevenueCat enter', {
    appUserId: appUserId.slice(0, 8),
    initState,
    apiKey: summarizeApiKeyForTrace(apiKey),
    productId: APPLE_PRODUCT_ID_PACK_LEADER,
    offeringId: REVENUECAT_OFFERING_IDENTIFIER,
    packageId: REVENUECAT_PACKAGE_IDENTIFIER,
  })
  if (!apiKey) {
    initState = 'error'
    initError = SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE
    offeringsLoaded = false
    logUpgradeIapBlocked('initializeRevenueCat_missing_api_key')
    console.error(
      '[RevenueCat] NEXT_PUBLIC_REVENUECAT_APPLE_API_KEY is missing'
    )
    return
  }

  if (initState === 'ready' && configuredUserId === appUserId) {
    logUpgradeIapTrace('initializeRevenueCat already ready', {
      appUserId: appUserId.slice(0, 8),
    })
    traceRcLog('initializeRevenueCat skipped', {
      reason: 'already_ready',
      appUserId: appUserId.slice(0, 8),
    })
    return
  }

  initState = 'initializing'
  initError = null
  offeringsLoaded = false

  try {
    const { Purchases } = await loadPurchasesModule()
    logUpgradeIapTrace('before Purchases.configure', {
      appUserId: appUserId.slice(0, 8),
    })
    traceRcLog('Purchases.configure start', {
      appUserId: appUserId.slice(0, 8),
    })
    await Purchases.configure({
      apiKey,
      appUserID: appUserId,
    })
    logUpgradeIapTrace('Purchases.configure success', {
      appUserId: appUserId.slice(0, 8),
    })
    traceRcLog('Purchases.configure success')
    if (configuredUserId && configuredUserId !== appUserId) {
      await Purchases.logIn({ appUserID: appUserId })
    }
    configuredUserId = appUserId
    initState = 'ready'
    logIapFinalTrace('Purchases.configure success', {
      appUserId: appUserId.slice(0, 8),
    })

    try {
      await preloadPackLeaderOfferings('post_configure')
    } catch (offeringsError) {
      logIapFinalTrace('post_configure offerings failed', {
        message:
          offeringsError instanceof Error
            ? offeringsError.message
            : String(offeringsError),
        code: (offeringsError as { code?: string })?.code ?? null,
      })
    }
  } catch (error) {
    initState = 'error'
    initError = SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE
    offeringsLoaded = false
    logUpgradeIapTrace('Purchases.configure error', {
      message: error instanceof Error ? error.message : String(error),
    })
    traceRcFail('Purchases.configure fail', error)
    console.error('[RevenueCat] initialize failed:', error)
    throw error
  }
}

/**
 * Fetch offerings and mark ready when the Pack Leader package is available.
 */
export async function preloadPackLeaderOfferings(
  context = 'preload'
): Promise<boolean> {
  if (initState !== 'ready') {
    offeringsLoaded = false
    return false
  }

  try {
    const { Purchases } = await loadPurchasesModule()
    traceRcLog('Purchases.getOfferings start', { context })
    const offerings = await Purchases.getOfferings()
    const offering =
      offerings.all?.[REVENUECAT_OFFERING_IDENTIFIER] ?? offerings.current
    const pkg = findPackLeaderPackage(offering)

    if (!pkg?.product) {
      offeringsLoaded = false
      console.log('[UpgradeModal] offerings loaded', {
        context,
        ready: false,
        offeringId: offering?.identifier ?? null,
        packageCount: offering?.availablePackages?.length ?? 0,
        packageIds: offering?.availablePackages?.map((p) => p.identifier) ?? [],
      })
      return false
    }

    offeringsLoaded = true
    console.log('[UpgradeModal] offerings loaded', {
      context,
      ready: true,
      offeringId: offering?.identifier ?? null,
      packageId: pkg.identifier,
      productId: pkg.product.identifier,
      priceString: pkg.product.priceString ?? null,
    })
    return true
  } catch (error) {
    offeringsLoaded = false
    traceRcFail('preloadPackLeaderOfferings fail', error)
    console.log('[UpgradeModal] offerings loaded', {
      context,
      ready: false,
      error: error instanceof Error ? error.message : String(error),
    })
    return false
  }
}

/**
 * Configure RevenueCat, wait for init, and preload offerings before purchase.
 */
export async function ensureRevenueCatReadyForPurchase(
  appUserId: string
): Promise<boolean> {
  if (!isNativeIosRevenueCatContext()) return false

  try {
    await initializeRevenueCat(appUserId)
  } catch {
    /* initState updated inside initializeRevenueCat */
  }

  if (initState === 'initializing') {
    await waitForRevenueCatReady(8000)
  }

  if (initState !== 'ready') {
    offeringsLoaded = false
    console.log('[UpgradeModal] ensureRevenueCatReadyForPurchase', {
      ready: false,
      reason: 'init_not_ready',
      initState,
      initError,
    })
    return false
  }

  const loaded =
    offeringsLoaded ||
    (await preloadPackLeaderOfferings('ensure_ready_for_purchase'))
  console.log('[UpgradeModal] ensureRevenueCatReadyForPurchase', {
    ready: true,
    initState,
    offeringsLoaded: loaded,
  })
  // RC configured — proceed to purchasePackage even if offerings preload failed;
  // purchasePackLeaderViaRevenueCat will retry getOfferings and log package/product resolution.
  return true
}

export type IapPackagePrice = {
  priceString: string
  productId: string
}

/**
 * Load default offering / $rc_monthly package price for display in UpgradeModal.
 */
export async function fetchPackLeaderPackagePrice(): Promise<IapPackagePrice | null> {
  if (!isNativeIosRevenueCatContext() || initState !== 'ready') return null

  try {
    const { Purchases } = await loadPurchasesModule()
    traceRcLog('Purchases.getOfferings start', {
      context: 'fetchPackLeaderPackagePrice',
    })
    const offerings = await Purchases.getOfferings()
    traceRcLog('Purchases.getOfferings success', {
      context: 'fetchPackLeaderPackagePrice',
      current: offerings.current?.identifier ?? null,
    })
    const offering =
      offerings.all?.[REVENUECAT_OFFERING_IDENTIFIER] ?? offerings.current
    const pkg = findPackLeaderPackage(offering)
    if (!pkg?.product) return null

    const productId = pkg.product.identifier
    if (productId !== APPLE_PRODUCT_ID_PACK_LEADER) {
      console.warn(
        '[RevenueCat] Unexpected product id on $rc_monthly:',
        productId
      )
    }

    return {
      priceString: pkg.product.priceString,
      productId,
    }
  } catch (error) {
    traceRcFail('Purchases.getOfferings fail', error)
    console.warn('[RevenueCat] fetchPackLeaderPackagePrice failed:', error)
    return null
  }
}

export type RevenueCatPurchaseResult =
  | {
      ok: true
      customerInfo: { entitlements?: { active?: Record<string, unknown> } }
      alreadySubscribed?: boolean
    }
  | { ok: false; cancelled: boolean; code?: string; message: string }

function mapPurchaseError(error: unknown): RevenueCatPurchaseResult {
  const err = error as {
    code?: string | number
    message?: string
    userCancelled?: boolean
    readableErrorCode?: string
    underlyingErrorMessage?: string
  }
  const code = err?.code != null ? String(err.code) : undefined
  const message = err?.message || 'Purchase failed'

  logIapFinalTrace('mapPurchaseError', {
    code: code ?? null,
    message,
    readableErrorCode: err?.readableErrorCode ?? null,
    underlyingErrorMessage: err?.underlyingErrorMessage ?? null,
    userCancelled: err?.userCancelled ?? null,
  })

  if (
    code === '1' ||
    code === 'PURCHASE_CANCELLED_ERROR' ||
    err?.userCancelled === true ||
    /cancel/i.test(message)
  ) {
    return { ok: false, cancelled: true, code, message: 'Purchase cancelled' }
  }

  if (
    code === '23' ||
    code === 'CONFIGURATION_ERROR' ||
    /configuration|not available|could not be fetched|problem with the app store|store/i.test(
      message
    )
  ) {
    return {
      ok: false,
      cancelled: false,
      code,
      message: /problem with the app store/i.test(message)
        ? 'There was a problem with the App Store. Confirm sandbox sign-in, StoreKit product setup, and try again.'
        : 'Subscription is not available right now. Please try again later.',
    }
  }

  if (code === '10' || code === 'NETWORK_ERROR') {
    return {
      ok: false,
      cancelled: false,
      code,
      message: 'Network error. Check your connection and try again.',
    }
  }

  return { ok: false, cancelled: false, code, message: sanitizePurchaseFailureMessage(message) }
}

/**
 * Purchase Pack Leader via RevenueCat default offering / $rc_monthly package.
 */
export async function purchasePackLeaderViaRevenueCat(): Promise<RevenueCatPurchaseResult> {
  logIapFinalTrace('purchasePackLeaderViaRevenueCat enter', {
    initState,
    initError,
    configuredUserId: configuredUserId ? configuredUserId.slice(0, 8) : null,
    apiKey: summarizeApiKeyForTrace(getAppleApiKey()),
    productId: APPLE_PRODUCT_ID_PACK_LEADER,
    isNativeIos: isNativeIosRevenueCatContext(),
  })

  if (!isNativeIosRevenueCatContext()) {
    logUpgradeIapBlocked('purchase_not_native_ios')
    traceRcLog('purchasePackLeaderViaRevenueCat skipped', {
      reason: 'not_native_ios',
    })
    return { ok: false, cancelled: false, message: 'Not on native iOS' }
  }

  if (initState === 'initializing') {
    logIapFinalTrace('purchase waiting for init', { initState })
    const ready = await waitForRevenueCatReady(8000)
    logIapFinalTrace('purchase wait for init result', {
      ready,
      initState,
      initError,
    })
    if (!ready) {
      return {
        ok: false,
        cancelled: false,
        message:
          initError ||
          'Purchases are still preparing. Please wait a moment and try again.',
      }
    }
  }

  if (initState !== 'ready') {
    logIapFinalTrace('purchase blocked not ready', { initState, initError })
    logUpgradeIapBlocked('purchase_revenuecat_not_ready', {
      initState,
      initError,
    })
    traceRcLog('purchasePackLeaderViaRevenueCat not ready', {
      initState,
      initError,
    })
    return {
      ok: false,
      cancelled: false,
      message:
        initError ||
        'Purchases are not ready yet. Please wait a moment and try again.',
    }
  }

  try {
    const { Purchases } = await loadPurchasesModule()
    let offerings
    try {
      logUpgradeIapTrace('before getOfferings', {
        offeringId: REVENUECAT_OFFERING_IDENTIFIER,
        packageId: REVENUECAT_PACKAGE_IDENTIFIER,
        productId: APPLE_PRODUCT_ID_PACK_LEADER,
      })
      traceRcLog('Purchases.getOfferings start', {
        context: 'purchasePackLeaderViaRevenueCat',
      })
      offerings = await Purchases.getOfferings()
      const offeringPreview =
        offerings.all?.[REVENUECAT_OFFERING_IDENTIFIER] ?? offerings.current
      const pkgPreview = findPackLeaderPackage(offeringPreview)
      logUpgradeIapTrace('getOfferings result', {
        currentOfferingId: offerings.current?.identifier ?? null,
        targetOfferingId: offeringPreview?.identifier ?? null,
        packageId: pkgPreview?.identifier ?? null,
        productId: pkgPreview?.product?.identifier ?? null,
        packageCount: offeringPreview?.availablePackages?.length ?? 0,
        packageIds:
          offeringPreview?.availablePackages?.map(
            (p: { identifier: string }) => p.identifier
          ) ?? [],
      })
      traceRcLog('Purchases.getOfferings success', {
        context: 'purchasePackLeaderViaRevenueCat',
        current: offerings.current?.identifier ?? null,
      })
    } catch (error) {
      logUpgradeIapTrace('getOfferings error', {
        message: error instanceof Error ? error.message : String(error),
      })
      traceRcFail('Purchases.getOfferings fail', error)
      throw error
    }
    const offering =
      offerings.all?.[REVENUECAT_OFFERING_IDENTIFIER] ?? offerings.current

    logUpgradeFlow('RevenueCat offering/package', {
      offeringId: offering?.identifier ?? null,
      packageId: REVENUECAT_PACKAGE_IDENTIFIER,
      productId: findPackLeaderPackage(offering)?.product?.identifier ?? null,
    })

    if (!offering) {
      logUpgradeIapBlocked('offering_unavailable', {
        offeringId: REVENUECAT_OFFERING_IDENTIFIER,
      })
      return {
        ok: false,
        cancelled: false,
        message:
          'Subscription offering is unavailable. Please try again later.',
      }
    }

    const pkg = findPackLeaderPackage(offering)

    if (!pkg) {
      logUpgradeIapBlocked('package_unavailable', {
        packageId: REVENUECAT_PACKAGE_IDENTIFIER,
        offeringId: offering.identifier,
        packageIds:
          offering.availablePackages?.map(
            (p: { identifier: string }) => p.identifier
          ) ?? [],
      })
      console.log('[UpgradeModal] purchase failed', {
        reason: 'package_not_found',
        offeringId: offering.identifier,
        expectedPackageId: REVENUECAT_PACKAGE_IDENTIFIER,
        expectedProductId: APPLE_PRODUCT_ID_PACK_LEADER,
      })
      return {
        ok: false,
        cancelled: false,
        message:
          'Pack Leader subscription is unavailable. Please try again later.',
      }
    }

    if (pkg.product.identifier !== APPLE_PRODUCT_ID_PACK_LEADER) {
      console.warn(
        '[RevenueCat] Package product mismatch:',
        pkg.product.identifier,
        'expected',
        APPLE_PRODUCT_ID_PACK_LEADER
      )
    }

    console.log('[UpgradeModal] product selected', {
      packageId: pkg.identifier,
      productId: pkg.product.identifier,
      priceString: pkg.product.priceString ?? null,
      offeringId: offering.identifier,
    })

    logUpgradeIapTrace('before purchasePackage', {
      packageId: pkg.identifier,
      productId: pkg.product.identifier,
    })
    traceRcLog('Purchases.purchasePackage start', {
      packageId: pkg.identifier,
      productId: pkg.product.identifier,
    })
    logIapFinalTrace('before purchasePackage', {
      packageId: pkg.identifier,
      productId: pkg.product.identifier,
      priceString: pkg.product.priceString ?? null,
      offeringId: offering.identifier,
    })
    console.log('[UpgradeModal] purchase started', {
      packageId: pkg.identifier,
      productId: pkg.product.identifier,
    })
    setNativePaymentInProgress(true)
    let customerInfo
    try {
      ;({ customerInfo } = await Purchases.purchasePackage({ aPackage: pkg }))
    } finally {
      setNativePaymentInProgress(false)
    }
    const customerInfoSummary =
      summarizeCustomerInfoForUpgradeTrace(customerInfo)
    logUpgradeIapTrace(
      'purchasePackage result full customerInfo summary',
      customerInfoSummary
    )
    logUpgradeIapTrace('active entitlement keys', {
      keys: customerInfoSummary.activeEntitlementKeys,
    })
    logUpgradeIapTrace('all entitlement keys', {
      keys: customerInfoSummary.allEntitlementKeys,
    })
    logUpgradeIapTrace('expected entitlement id', {
      expectedEntitlementId: REVENUECAT_ENTITLEMENT_PACK_LEADER,
    })
    logUpgradeIapTrace('purchasePackage result', {
      packageId: pkg.identifier,
      productId: pkg.product.identifier,
      hasPackLeader: hasPackLeaderEntitlement(customerInfo),
    })
    traceRcLog('Purchases.purchasePackage success', {
      hasPackLeader: hasPackLeaderEntitlement(customerInfo),
    })

    let retryCustomerInfo: {
      entitlements?: { active?: Record<string, unknown> }
    } | null = null
    try {
      const retryResult = await Purchases.getCustomerInfo()
      retryCustomerInfo = retryResult.customerInfo
      const retrySummary =
        summarizeCustomerInfoForUpgradeTrace(retryCustomerInfo)
      logUpgradeIapTrace('getCustomerInfo retry result', {
        ...retrySummary,
        hasExpectedEntitlement: hasPackLeaderEntitlement(retryCustomerInfo),
      })
    } catch (retryError) {
      logUpgradeIapTrace('getCustomerInfo retry result', {
        error:
          retryError instanceof Error ? retryError.message : String(retryError),
      })
    }

    if (!customerInfoGrantsPackLeaderAccess(customerInfo, 'post_purchase_package')) {
      let resolvedCustomerInfo: PackLeaderCustomerInfo | null = null
      if (
        retryCustomerInfo &&
        customerInfoGrantsPackLeaderAccess(
          retryCustomerInfo,
          'post_purchase_getCustomerInfo_retry'
        )
      ) {
        resolvedCustomerInfo = retryCustomerInfo
      } else if (
        customerInfoGrantsPackLeaderAccess(
          customerInfo,
          'post_purchase_package_recheck'
        )
      ) {
        resolvedCustomerInfo = customerInfo
      } else {
        resolvedCustomerInfo = await resolveActivePackLeaderCustomerInfo(
          'post_purchase_missing_entitlement'
        )
      }

      if (!resolvedCustomerInfo) {
        logUpgradeIapTrace('verification failed reason', {
          stage: 'client_purchasePackage_customerInfo',
          reason: 'active_entitlement_missing_after_purchasePackage',
          expectedEntitlementId: REVENUECAT_ENTITLEMENT_PACK_LEADER,
          expectedProductId: APPLE_PRODUCT_ID_PACK_LEADER,
          activeEntitlementKeys: customerInfoSummary.activeEntitlementKeys,
          allEntitlementKeys: customerInfoSummary.allEntitlementKeys,
          retryHadExpectedEntitlement: retryCustomerInfo
            ? customerInfoGrantsPackLeaderAccess(
                retryCustomerInfo,
                'post_purchase_retry_check'
              )
            : null,
          packageId: pkg.identifier,
          productId: pkg.product.identifier,
        })
        logUpgradeIapBlocked('purchase_missing_entitlement_after_success', {
          packageId: pkg.identifier,
          productId: pkg.product.identifier,
        })
        return {
          ok: false,
          cancelled: false,
          message: SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE,
        }
      }
      customerInfo = resolvedCustomerInfo
    }

    console.log('[UpgradeModal] purchase completed', {
      packageId: pkg.identifier,
      productId: pkg.product.identifier,
    })
    return { ok: true, customerInfo }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))
    logIapFinalTrace('purchasePackage exception', {
      message: err.message,
      name: err.name,
      code: (error as { code?: string | number })?.code ?? null,
      readableErrorCode:
        (error as { readableErrorCode?: string })?.readableErrorCode ?? null,
      underlyingErrorMessage:
        (error as { underlyingErrorMessage?: string })
          ?.underlyingErrorMessage ?? null,
      alreadySubscribed: isAlreadySubscribedPurchaseError(error),
    })
    logUpgradeIapTrace('purchasePackage error', {
      message: err.message,
      name: err.name,
      code: (error as { code?: string })?.code,
      alreadySubscribed: isAlreadySubscribedPurchaseError(error),
    })
    traceRcFail('Purchases.purchasePackage fail', error)
    console.error('[RevenueCat] purchase failed:', error)

    const activeCustomerInfo =
      await resolveActivePackLeaderAfterPurchaseFailure(error)
    if (activeCustomerInfo) {
      const alreadySubscribed = isAlreadySubscribedPurchaseError(error)
      logUpgradeIapTrace('purchase success via customerInfo after purchase attempt', {
        alreadySubscribed,
        hasPackLeader: true,
      })
      console.log('[UpgradeModal] purchase completed', {
        reason: alreadySubscribed
          ? 'existing_subscription'
          : 'active_entitlement_after_error',
        productId: APPLE_PRODUCT_ID_PACK_LEADER,
      })
      forceClearNativeInteractionLocks('revenuecat_purchase_existing_subscription')
      return {
        ok: true,
        customerInfo: activeCustomerInfo,
        alreadySubscribed,
      }
    }

    const result = mapPurchaseError(error)
    if (!result.ok && result.cancelled) {
      console.log('[UpgradeModal] purchase cancelled', {
        packageId: REVENUECAT_PACKAGE_IDENTIFIER,
        productId: APPLE_PRODUCT_ID_PACK_LEADER,
      })
      forceClearNativeInteractionLocks('revenuecat_purchase_cancelled')
      return result
    }

    if (!result.ok && isAlreadySubscribedPurchaseError(error)) {
      const recovered = await tryApplyPackLeaderTierFromRevenueCat(
        'purchase_already_subscribed_fallback'
      )
      if (recovered.applied && recovered.customerInfo) {
        forceClearNativeInteractionLocks('revenuecat_purchase_existing_subscription')
        return {
          ok: true,
          customerInfo: recovered.customerInfo,
          alreadySubscribed: true,
        }
      }
      forceClearNativeInteractionLocks('revenuecat_purchase_already_subscribed')
      return {
        ok: false,
        cancelled: false,
        message: SUBSCRIPTION_PURCHASE_COMPLETED_REFRESHING_MESSAGE,
      }
    }

    if (!result.ok) {
      console.log('[UpgradeModal] purchase failed', {
        packageId: REVENUECAT_PACKAGE_IDENTIFIER,
        productId: APPLE_PRODUCT_ID_PACK_LEADER,
        code: result.code ?? null,
      })
      forceClearNativeInteractionLocks('revenuecat_purchase_error')
    }
    return result
  }
}

export type RevenueCatRestoreResult =
  | {
      ok: true
      restored: boolean
      customerInfo: { entitlements?: { active?: Record<string, unknown> } }
    }
  | { ok: false; message: string; code?: string }

/**
 * Restore App Store purchases and refresh RevenueCat entitlements.
 */
export async function restorePurchasesViaRevenueCat(): Promise<RevenueCatRestoreResult> {
  if (!isNativeIosRevenueCatContext()) {
    return { ok: false, message: 'Restore is only available in the iOS app.' }
  }

  if (initState !== 'ready') {
    return {
      ok: false,
      message:
        initError ||
        'Purchases are not ready yet. Please wait a moment and try again.',
    }
  }

  try {
    const { Purchases } = await loadPurchasesModule()
    const { customerInfo } = await Purchases.restorePurchases()
    const restored = customerInfoGrantsPackLeaderAccess(
      customerInfo,
      'restorePurchases_success'
    )
    logUpgradeIapTrace('restorePurchases result', {
      ...summarizeCustomerInfoForUpgradeTrace(customerInfo),
      hasPackLeader: restored,
    })
    return { ok: true, restored, customerInfo }
  } catch (error) {
    console.error('[RevenueCat] restore failed:', error)
    const activeCustomerInfo = await fetchActivePackLeaderCustomerInfo(
      'restore_failure_getCustomerInfo'
    )
    if (activeCustomerInfo) {
      return { ok: true, restored: true, customerInfo: activeCustomerInfo }
    }
    const mapped = mapPurchaseError(error)
    if (mapped.ok) {
      return {
        ok: false,
        message: sanitizePaywallUserMessage(null, 'restore_failed'),
      }
    }
    return {
      ok: false,
      message: sanitizePaywallUserMessage(mapped.message, 'restore_failed'),
      code: mapped.code,
    }
  }
}

export { hasPackLeaderEntitlement, REVENUECAT_ENTITLEMENT_PACK_LEADER }
